import React from 'react';
import { Video, Play, Image as ImageIcon } from 'lucide-react';
import type { BlockRenderProps } from './types';

export const VideoPlayerRenderer: React.FC<BlockRenderProps> = ({ node }) => {
  const title = (node.props?.title as string) || 'Inside Helios Coach: Campus Walkthrough & Faculty Orientation';
  const videoURL = (node.props?.videoURL as string) || 'https://www.youtube-nocookie.com/embed/dQw4w9WgXcQ';
  const caption = (node.props?.caption as string) || 'Watch our founder discuss our 2-year entrance preparation pedagogy and mentorship system.';
  const aspectRatio = (node.props?.aspectRatio as string) || '16:9';

  const aspectClass =
    aspectRatio === '4:3'
      ? 'aspect-4/3'
      : aspectRatio === '1:1'
      ? 'aspect-square'
      : 'aspect-video';

  return (
    <div className="w-full my-6 text-center">
      <div className="flex items-center justify-center gap-1.5 text-xs font-bold text-primary uppercase tracking-wider mb-2">
        <Video className="h-3.5 w-3.5" />
        <span>Featured Video</span>
      </div>
      <h3 className="text-xl md:text-2xl font-bold text-foreground mb-3">{title}</h3>

      <div className={`w-full max-w-4xl mx-auto rounded-2xl overflow-hidden border border-border shadow-lg bg-black ${aspectClass} relative`}>
        <iframe
          src={videoURL}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full border-none"
        />
      </div>

      {caption && (
        <p className="text-xs text-muted-foreground max-w-xl mx-auto mt-3 italic leading-relaxed">
          {caption}
        </p>
      )}
    </div>
  );
};

export const ImageGalleryRenderer: React.FC<BlockRenderProps> = ({ node, isEditor }) => {
  const title = (node.props?.title as string) || 'Campus & Classroom Gallery';
  const customImages = Array.isArray(node.props?.images) && node.props.images.length > 0 ? (node.props.images as string[]) : [];

  const sampleImages = isEditor
    ? [
        'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=600&auto=format&fit=crop&q=80',
        'https://images.unsplash.com/photo-1509062522246-3755977927d7?w=600&auto=format&fit=crop&q=80',
        'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?w=600&auto=format&fit=crop&q=80',
        'https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=600&auto=format&fit=crop&q=80',
      ]
    : [];

  const displayImages = customImages.length > 0 ? customImages : sampleImages;

  if (displayImages.length === 0) {
    return null;
  }

  return (
    <div className="py-6 w-full">
      <div className="flex items-center gap-2 mb-4">
        <ImageIcon className="h-4 w-4 text-primary" />
        <h3 className="text-lg font-bold text-foreground">{title}</h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {displayImages.map((src, i) => (
          <div key={i} className="aspect-4/3 rounded-xl overflow-hidden border border-border bg-muted shadow-xs hover:opacity-95 transition-opacity">
            <img src={src} alt={`Gallery item ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
          </div>
        ))}
      </div>
    </div>
  );
};
